import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTSpinner from '../common/RTSpinner';
import RTModal from '../common/RTModal';
import InvoiceSendSummaryModal from './InvoiceSendSummaryModal';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './ViewDraftInvoices.css';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import RTPageLayout from '../common/RTPageLayout';

const ViewDraftInvoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [generatePaymentLink, setGeneratePaymentLink] = useState(true);
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [filters, setFilters] = useState({
    building_id: undefined,
    room_type: undefined,
    occupancy_gender: undefined,
  });

  const filterFields = useMemo(
    () => [
      {
        key: 'building_id',
        label: 'Building',
        type: 'autocomplete',
        width: 'medium',
        placeholder: 'Search building...',
        options: buildingOptions,
        applyImmediately: true,
      },
      {
        key: 'room_type',
        label: 'Room Type',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All' },
          { value: 'AC', label: 'AC' },
          { value: 'Non AC', label: 'Non AC' },
        ],
        applyImmediately: true,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
        applyImmediately: true,
      },
    ],
    [buildingOptions],
  );

  useEffect(() => {
    loadDraftInvoices(filters);
    loadPaymentProvider();
    loadBuildingOptions();
  }, []);

  const loadBuildingOptions = async () => {
    try {
      const response = await apiGet('/building/names?status=Active');
      setBuildingOptions(
        (response.data || []).map((b) => ({
          value: String(b.building_id),
          label: b.name,
        })),
      );
    } catch (error) {
      console.error('Error loading buildings:', error);
    }
  };

  const loadPaymentProvider = async () => {
    try {
      const response = await apiGet('/payments/settings');
      const data = response.data;
      if (data.payment_provider?.value) {
        try {
          const providerData = JSON.parse(data.payment_provider.value);
          const selected = providerData.selected || 'none';
          setPaymentProvider(selected);
          if (selected === 'none') {
            setGeneratePaymentLink(false);
          }
        } catch (e) {
          const val = data.payment_provider.value;
          setPaymentProvider(val);
          if (val === 'none') {
            setGeneratePaymentLink(false);
          }
        }
      } else {
        setPaymentProvider('none');
        setGeneratePaymentLink(false);
      }
    } catch (error) {
      console.error('Error loading payment provider:', error);
      setPaymentProvider('none');
      setGeneratePaymentLink(false);
    }
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
  };

  const loadDraftInvoices = async (filterParams = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('status', 'draft');
      params.append('limit', '1000');
      params.append('order_by', 'building');
      if (filterParams.building_id) {
        params.append('building_id', filterParams.building_id);
      }
      if (filterParams.room_type) {
        params.append('room_type', filterParams.room_type);
      }
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      const response = await apiGet(`/invoicing/invoices?${params.toString()}`);
      if (response && response.data) {
        const data = response.data;
        // Handle both old format (array) and new format (object with invoices and totalInvoicesAmount)
        if (Array.isArray(data)) {
          setInvoices(data);
          // Calculate total if not provided
          const total = data.reduce(
            (sum, inv) => sum + (inv.total_amount || 0),
            0,
          );
          setTotalAmount(total);
        } else {
          setInvoices(data.invoices || []);
          setTotalAmount(data.totalInvoicesAmount || 0);
        }
      }
    } catch (error) {
      console.error('Error loading draft invoices:', error);
      toastError('Failed to load draft invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFiltersAndFetch = (updates) => {
    const next = { ...filters, ...updates };
    setFilters(next);
    loadDraftInvoices(next);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'building_id' && (value === undefined || value === '')) {
      setBuildingSearch('');
    }
    updateFiltersAndFetch({ [key]: value || undefined });
  };

  const handleFilterSearchChange = (key, text) => {
    if (key === 'building_id') {
      setBuildingSearch(text);
      if (!text) {
        updateFiltersAndFetch({ building_id: undefined });
      }
    }
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (filters.building_id) {
      const buildingLabel =
        buildingSearch ||
        buildingOptions.find((o) => o.value === String(filters.building_id))
          ?.label ||
        filters.building_id;
      pills.push({ key: 'building_id', label: `Building: ${buildingLabel}` });
    }
    if (filters.room_type) {
      pills.push({
        key: 'room_type',
        label: `Room Type: ${filters.room_type}`,
      });
    }
    if (filters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === filters.occupancy_gender,
        )?.label || filters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    return pills;
  }, [filters, buildingSearch, buildingOptions]);

  const handleRemoveAppliedFilter = (key) => {
    if (key === 'building_id') {
      setBuildingSearch('');
      updateFiltersAndFetch({ building_id: undefined });
    } else if (key === 'room_type') {
      updateFiltersAndFetch({ room_type: undefined });
    }
  };

  const handleClearAllAppliedFilters = () => {
    setBuildingSearch('');
    const next = { building_id: undefined, room_type: undefined };
    setFilters(next);
    loadDraftInvoices(next);
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
    } else {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allInvoiceIds = invoices.map((invoice) => invoice.invoice_id);
      setSelectedInvoices(allInvoiceIds);
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSendInvoices = async () => {
    if (selectedInvoices.length === 0) {
      toastError('Please select at least one invoice to send.');
      return;
    }
    setShowSendModal(true);
  };

  const confirmSendInvoices = async () => {
    try {
      // Close the send modal and immediately show processing in summary modal
      setShowSendModal(false);
      setProcessing(true);

      // Show processing state immediately before API call
      setSummaryData({
        job_id: null,
        total_invoices: selectedInvoices.length,
        sent_count: 0,
        failed_count: 0,
        payment_link_success_count: 0,
        payment_link_failed_count: 0,
        whatsapp_sent_count: 0,
        whatsapp_failed_count: 0,
        status: 'starting',
        message: 'Starting background processing...',
      });
      setShowSummaryModal(true);

      // Call the background send_invoices API with reduced timeout (10 seconds)
      // Response should come back immediately with job_id since it's background processing
      console.log(
        `Starting background processing for ${selectedInvoices.length} invoices...`,
      );

      const response = await apiPost(
        '/invoicing/invoices/send',
        {
          invoice_ids: selectedInvoices,
          generate_payment_link: generatePaymentLink,
        },
        {
          timeout: 10000, // 10 second timeout - response should be immediate
        },
      );

      console.log('Invoice processing started in background:', response.data);

      if (response && response.data) {
        const result = response.data;

        // Store job info for tracking
        const jobId = result.job_id;
        console.log(
          `Job ID: ${jobId} - Processing ${result.total_invoices} invoices in background`,
        );

        // Update summary data with job ID
        setSummaryData({
          job_id: jobId,
          total_invoices: result.total_invoices,
          sent_count: 0,
          failed_count: 0,
          payment_link_success_count: 0,
          payment_link_failed_count: 0,
          whatsapp_sent_count: 0,
          whatsapp_failed_count: 0,
          status: 'processing',
          message:
            'Processing invoices in background. This may take several minutes. You can close this modal and continue working.',
        });

        // Clear selections after starting background job
        setSelectedInvoices([]);
        setSelectAll(false);

        // Reload the invoices
        await loadDraftInvoices(filters);
      }
    } catch (error) {
      console.error('Error sending invoices:', error);

      // Check if it was a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout means request may still be processing in background
        setSummaryData({
          job_id: null,
          total_invoices: selectedInvoices.length,
          sent_count: 0,
          failed_count: 0,
          payment_link_success_count: 0,
          payment_link_failed_count: 0,
          whatsapp_sent_count: 0,
          whatsapp_failed_count: 0,
          status: 'timeout',
          message:
            'Request timed out. The background job may still be running. Please check the Background Jobs report to verify.',
        });
        toastError(
          'Request timed out. Check Background Jobs report for processing status.',
        );

        // Clear selections
        setSelectedInvoices([]);
        setSelectAll(false);

        // Reload the invoices
        await loadDraftInvoices(filters);
      } else if (error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
        toastError('Request was canceled. Please try again.');
        setShowSummaryModal(false);
      } else {
        toastError(
          error.response?.data?.detail ||
            'Failed to send invoices. Please try again.',
        );
        setShowSummaryModal(false);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteInvoices = async () => {
    if (selectedInvoices.length === 0) {
      toastError('Please select at least one invoice to delete.');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDeleteInvoices = async () => {
    try {
      setProcessing(true);

      // Update each selected invoice status to 'cancelled'
      const updatePromises = selectedInvoices.map((invoiceId) =>
        apiPost(`/invoicing/invoices/${invoiceId}/update`, {
          status: 'cancelled',
        }),
      );

      await Promise.all(updatePromises);

      toastSuccess(
        `Successfully deleted ${selectedInvoices.length} invoice(s).`,
      );
      setSelectedInvoices([]);
      setSelectAll(false);
      setShowDeleteModal(false);

      // Reload the invoices
      await loadDraftInvoices(filters);
    } catch (error) {
      console.error('Error deleting invoices:', error);
      toastError('Failed to delete invoices. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewEditInvoice = (invoice) => {
    window.open(`/payments/invoices/${invoice.invoice_id}/details`, '_blank');
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const prepareTableData = () => {
    return invoices.map((invoice) => ({
      id: invoice.invoice_id,
      invoice_id: invoice.invoice_id,
      invoice_number: invoice.invoice_number,
      pg_id: invoice.pg_id || invoice.paying_guest?.pg_id,
      pg_name: invoice.paying_guest?.name || invoice.pg?.name || 'N/A',
      building_id: invoice.building_id || invoice.building?.id,
      building_name: invoice.building?.name || 'N/A',
      room_id: invoice.room_id,
      room_name: invoice.room_name || invoice.room?.room_name || 'N/A',
      invoice_date: new Date(invoice.invoice_date).toLocaleDateString(),
      items: invoice.items_text || 'No items',
      total_amount: invoice.total_amount,
      invoice: invoice,
    }));
  };

  const columns = [
    {
      Header: ({ value }) => (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="checkbox-input"
        />
      ),
      disableExport: true,
      accessor: 'select',
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedInvoices.includes(row.invoice_id)}
          onChange={(e) =>
            handleSelectInvoice(row.invoice_id, e.target.checked)
          }
          className="checkbox-input"
        />
      ),
      width: 50,
    },
    {
      Header: 'Invoice #',
      accessor: 'invoice_number',
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) =>
        row.building_id ? (
          <RTButton
            type="link"
            onClick={() => handleBuildingClick(row.building_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (
          value || 'N/A'
        ),
    },
    {
      Header: 'Room',
      accessor: 'room_name',
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) =>
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (
          value || 'N/A'
        ),
    },
    {
      Header: 'Invoice Date',
      accessor: 'invoice_date',
    },
    {
      Header: 'Items',
      accessor: 'items',
    },
    {
      Header: 'Total Amount',
      accessor: 'total_amount',
      Cell: ({ row }) =>
        `₹${parseFloat(row.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableExport: true,
      Cell: ({ row }) => (
        <div className="action-buttons">
          <RTButton
            type="link"
            size="small"
            onClick={() => handleViewEditInvoice(row.invoice)}
          >
            View
          </RTButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <RTPageLayout
        activePath={location.pathname}
        title="View Draft Invoices"
        actions={
          <>
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              ← Back
            </RTButton>
            <RTButton
              variant="primary"
              permissionKey="payments.create_invoice"
              onClick={handleSendInvoices}
              disabled={selectedInvoices.length === 0}
            >
              Send Invoices ({selectedInvoices.length})
            </RTButton>
            <RTButton
              variant="danger"
              permissionKey="payments.create_invoice"
              onClick={handleDeleteInvoices}
              disabled={selectedInvoices.length === 0}
            >
              Cancel Invoices
            </RTButton>
          </>
        }
        mainSection={true}
        contentClassName="view-draft-invoices"
      >
        <RTFilteredTable
          loading={loading}
          loadingMessage="Loading draft invoices..."
          filters={
            <RTFilters
              fields={filterFields}
              values={filters}
              searchValues={{ building_id: buildingSearch }}
              onChange={handleFilterChange}
              onSearchChange={handleFilterSearchChange}
              pills={appliedFilterPills}
              onRemovePill={handleRemoveAppliedFilter}
              onClearAll={handleClearAllAppliedFilters}
            />
          }
        >
          <Table
            title={
              invoices.length > 0
                ? `Draft Invoices (${invoices.length} total) - Total Amount: ₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'Draft Invoices'
            }
            columns={columns}
            data={prepareTableData()}
            showSearch={true}
            exportable={true}
            exportFileName="draft_invoices"
            showPagination={true}
            pageSize={50}
            noDataText="No draft invoices found"
            disableSort={true}
            rowGroupConfig={{
              groups: [{ key: 'room_id', columns: ['room_name'] }],
            }}
          />
        </RTFilteredTable>
        {invoices.length === 0 && (
          <div className="no-data">
            <RTButton
              variant="primary"
              onClick={() => navigate('/payments/view-unpaid-invoices')}
            >
              View UnPaid Invoices
            </RTButton>
            <RTButton
              variant="secondary"
              onClick={() => navigate('/payments/generate-monthly')}
            >
              Generate Monthly Invoices
            </RTButton>
          </div>
        )}
      </RTPageLayout>

      {/* Send Confirmation Modal */}
      <RTModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Invoices"
        size="medium"
      >
        <div className="modal-content">
          <p>
            You are about to send <strong>{selectedInvoices.length}</strong>{' '}
            invoice(s) to the respective paying guests. Once sent, the invoices
            will be marked as "Sent" and cannot be edited.
          </p>

          <div className="form-check" style={{ margin: '20px 0' }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="generatePaymentLinkCheckbox"
              checked={generatePaymentLink}
              onChange={(e) => setGeneratePaymentLink(e.target.checked)}
              disabled={paymentProvider === 'none'}
            />
            <label
              className="form-check-label"
              htmlFor="generatePaymentLinkCheckbox"
            >
              Generate payment link for each invoice
            </label>
            {paymentProvider === 'none' && (
              <div className="text-muted small mt-1">
                No payment provider configured. Go to Settings &gt; Payment
                Settings to enable.
              </div>
            )}
          </div>

          <p>
            <strong>Are you sure you want to proceed?</strong>
          </p>
          <div className="modal-actions">
            <RTButton
              variant="secondary"
              onClick={() => setShowSendModal(false)}
              disabled={processing}
            >
              Cancel
            </RTButton>
            <RTButton
              variant="primary"
              onClick={confirmSendInvoices}
              loading={processing}
              disabled={processing}
            >
              {processing ? 'Sending...' : 'Send Invoices'}
            </RTButton>
          </div>
        </div>
      </RTModal>

      {/* Cancel Confirmation Modal */}
      <RTModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Cancel Invoices"
        size="medium"
      >
        <div className="modal-content">
          <p>
            You are about to cancel <strong>{selectedInvoices.length}</strong>{' '}
            invoice(s). This will mark them as "Cancelled" and they cannot be
            recovered.
          </p>
          <p>Are you sure you want to proceed?</p>
          <div className="modal-actions">
            <RTButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={processing}
            >
              Back
            </RTButton>
            <RTButton
              variant="primary"
              onClick={confirmDeleteInvoices}
              loading={processing}
              disabled={processing}
            >
              {processing ? 'Cancelling...' : 'Cancel Invoices'}
            </RTButton>
          </div>
        </div>
      </RTModal>

      {/* Summary Modal */}
      <InvoiceSendSummaryModal
        isOpen={showSummaryModal}
        onClose={handleCloseSummaryModal}
        summaryData={summaryData}
        generatePaymentLink={generatePaymentLink}
        isProcessing={processing}
      />
    </>
  );
};

export default ViewDraftInvoices;
